import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { localize } from 'core/localization';
import Trophy from 'assets/figures/Trophy';
import Rocket from 'assets/figures/Rocket';
import SadResult from 'assets/figures/SadResult';
import withNavigation from 'components/hocs/withNavigation';
import { StatusTooltip, Button, Link, CourseLogo } from 'components/common';
import * as courseSelectors from 'store/course/selectors';
import * as courseActions from 'store/course/actions';
import * as popupActions from 'store/popup/actions';
import * as navigationActions from 'store/navigation/actions';
import * as sectionActions from 'store/sections/actions';
import CloseCoursePopup from 'components/popups/closeCourse';
import NpsPopup from 'components/popups/nps';
import startNewAttempt from 'components/popups/startNewAttempt';
import CourseProgressResult from 'components/common/CourseProgressResult';
import { getQuestionUrl } from 'utils/navigation';
import {
  POPUP_ID_NPS_POPUP,
  POPUP_ID_CLOSE_COURSE,
  POPUP_ID_START_NEW_ATTEMPT
} from 'constants/popups';
import * as settingSelectors from 'store/settings/selectors';
import { isAuthorized, isAnonymous } from 'store/user/selectors';
import {
  getAffectProgressSectionsCount,
  getSectionsCount,
  getSectionsPassedCount
} from 'store/sections/selectors';
import certificateLoader from 'core/documentLoaders/certificateLoader';
import {
  hasScoreContentPages,
  getAffectProgressQuestionsCount,
  getCorrectlyAffectProgressAnsweredQuestionsCount,
  getFirstIncorrectOrPendingQuestion,
  hasReAnswerableQuestions
} from 'store/questions/selectors';
import { getTimerStatus } from 'store/timer/selectors';
import { TimerStatus } from 'constants/timer';
import { BUTTON, TOOLTIP, LINK } from 'constants/components';
import CourseAfterword from 'components/common/courseAfterword';
import { isLtiInitialized } from 'store/modules/selectors';
import { RootAppState } from 'store/types';
import RemainingAttemptsBlock from 'components/remainingAttemptsBlock';
import { SKIP_TO_CONTENT_IDS } from 'constants/common';
import {
  Container,
  Figure,
  Header,
  StyledText,
  ButtonWrapper,
  SeparatorElement,
  WidthLimiterContainer
} from './ResultsPage.styled';
import * as courseTimerActions from '../../../store/timer/actions';
import { isContrastTheme } from 'store/accessibility/selectors';

const MAX_SCORE = 100;

type ResultsPageProps = {
  isPassed?: boolean;
  isPreviewMode: boolean;
  isReviewMode: boolean;
  navigateToIndex(): void;
  navigateToUrl(url: string): void;
  navigateToPrevPage(): void;
  checkHasPrevPage(): any;
  affectProgressQuestionsCount: number;
  affectProgressSectionsCount: number;
  correctlyAnsweredQuestionsCount: number;
  title: string;
  isScormMode: boolean;
  isLti: boolean;
  courseActions: { [key: string]: any };
  courseId: string;
  templateId: string;
  score: number;
  logoUrl: string;
  logoShown: boolean;
  isCrossDeviceSavingEnabled: boolean;
  learnStorageUrl: string;
  selectedLanguage: string;
  courseHasBeenContinued: boolean;
  userAuthorized: boolean;
  isAllowedDownloadCertificate: boolean;
  certificateDownloaded: boolean;
  popupActions: { [key: string]: any };
  isNpsEnabled: boolean;
  isOverallMasteryScore: boolean;
  hasScoreContentPages: boolean;
  masteryScore: number;
  sectionsPassedCount: number;
  isInProgress?: boolean;
  shouldSubmitAllQuestions: boolean;
  isFailed?: boolean;
  navigationActions: { [key: string]: any };
  isAttemptsLimited?: boolean;
  isAnonymous?: boolean;
  hasReanswerableQuestions?: boolean;
  limitedAttemptsNumber: number;
  attemptsUsed: number;
  timerStatus: TimerStatus;
  sectionActions: { [key: string]: any };
  sectionCount: number;
  timerActions: { [key: string]: any };
  questionToNavigate: { [key: string]: any };
  isContrastTheme: boolean;
};

type ResultsPageState = {
  isCertificateLoading: boolean;
  showCertificateLoadedError: boolean;
  npsPopupWasOpened: boolean;
};
export class ResultsPage extends PureComponent<ResultsPageProps, ResultsPageState> {
  constructor(props: ResultsPageProps) {
    super(props);
    this.state = {
      isCertificateLoading: false,
      showCertificateLoadedError: false,
      npsPopupWasOpened: false
    };
  }

  continueCourse = () => {
    const {
      checkHasPrevPage,
      navigateToPrevPage,
      navigateToUrl,
      navigateToIndex,
      questionToNavigate,
      isAttemptsLimited
    } = this.props;

    if (!isAttemptsLimited && questionToNavigate) {
      navigateToUrl(getQuestionUrl(questionToNavigate.sectionId, questionToNavigate.id));
    } else if (checkHasPrevPage()) {
      navigateToPrevPage();
    } else {
      navigateToIndex();
    }
  };

  openNpsPopup({ certificateButton = false } = {}) {
    this.props.popupActions.openPopup({
      popupId: POPUP_ID_NPS_POPUP,
      popupAriaLabelKey: '[aria label close nps popup]',
      component: NpsPopup,
      fromDownloadCertificateButton: certificateButton,
      disablePopupClosing: true,
      disableBottomLine: true
    });
    this.setState({ npsPopupWasOpened: true });
  }

  hasRemainingAttempts = (): boolean => {
    const { limitedAttemptsNumber, attemptsUsed } = this.props;
    return limitedAttemptsNumber - attemptsUsed > 0;
  };

  startNewAttempt = async () => {
    const { navigateToUrl } = this.props;
    const urlToNavigate: any = await this.props.courseActions.startNewAttempt();
    await navigateToUrl(urlToNavigate);
  };

  openStartNewAttemptPopup = () => {
    const { navigateToIndex } = this.props;
    this.props.popupActions.openPopup({
      popupId: POPUP_ID_START_NEW_ATTEMPT,
      popupAriaLabelKey: '[aria label start new attempt popup]',
      component: startNewAttempt,
      newAttempt: this.props.courseActions.startNewAttempt,
      navigateToIndex,
      disablePopupClosing: false,
      disableBottomLine: true
    });
  };

  submitResults = async () => {
    await this.props.courseActions.finished();

    if (!this.props.isNpsEnabled) {
      this.props.popupActions.openPopup({
        popupId: POPUP_ID_CLOSE_COURSE,
        popupAriaLabelKey: '[aria label close course popup]',
        component: CloseCoursePopup,
        disablePopupClosing: true
      });
      await this.props.courseActions.finalized();
    }

    if (this.props.isNpsEnabled) {
      this.openNpsPopup();
    }
  };

  downloadCertificate = async () => {
    const data = {
      courseId: this.props.courseId,
      templateId: this.props.templateId,
      courseTitle: this.props.title,
      score: this.props.score,
      logoUrl: this.props.logoUrl
    };
    this.setState({ isCertificateLoading: true });

    const isCertificateDownloaded = await certificateLoader.downloadCertificate(
      data,
      this.props.learnStorageUrl,
      this.props.selectedLanguage
    );
    await this.props.courseActions.certificateDownloaded(isCertificateDownloaded);
    this.setState({
      isCertificateLoading: false,
      showCertificateLoadedError: !isCertificateDownloaded
    });

    if (this.props.isNpsEnabled && !this.state.npsPopupWasOpened) {
      this.openNpsPopup({ certificateButton: true });
    }
  };

  closeCourse = () => {
    const { isNpsEnabled, userAuthorized } = this.props;
    this.props.popupActions.openPopup({
      popupId:
        isNpsEnabled && !this.state.npsPopupWasOpened ? POPUP_ID_NPS_POPUP : POPUP_ID_CLOSE_COURSE,
      popupAriaLabelKey:
        isNpsEnabled && !this.state.npsPopupWasOpened
          ? '[aria label close nps popup]'
          : '[aria label close course popup]',
      component:
        isNpsEnabled && userAuthorized && !this.state.npsPopupWasOpened
          ? NpsPopup
          : CloseCoursePopup,
      disablePopupClosing: true,
      disableBottomLine: isNpsEnabled
    });
  };

  hidePopover = async () => {
    this.setState({ showCertificateLoadedError: false });
  };

  async componentDidMount() {
    document.title = `${localize('[title for results page]')} | ${this.props.title}`;
    await this.props.sectionActions.updateProgressUnaffectedSections();
    const { timerStatus, affectProgressQuestionsCount, timerActions } = this.props;
    if (timerStatus === TimerStatus.STARTED && affectProgressQuestionsCount === 0) {
      timerActions.setTimerStatus(TimerStatus.STOPPED);
      timerActions.updateTimerProgress();
    }
  }

  renderSubmitAtOnceButton() {
    const { isAttemptsLimited, isInProgress, isScormMode, isLti, score } = this.props;
    return (
      <>
        {isAttemptsLimited &&
          this.hasRemainingAttempts() &&
          score < 100 &&
          this.submitAtOnceStartNewAttemptButton()}
        {!isAttemptsLimited && this.submitAtOnceStartNewAttemptButton()}
        {!isInProgress && !isScormMode && !isLti && this.renderCloseCourseButton()}
      </>
    );
  }

  renderQuestionSubmitButton() {
    const {
      isPassed,
      isScormMode,
      isLti,
      isAttemptsLimited,
      isPreviewMode,
      isReviewMode,
      isInProgress,
      hasReanswerableQuestions,
      score,
      timerStatus
    } = this.props;
    const isTimerStopped = timerStatus === TimerStatus.STOPPED;
    return (
      <>
        {isInProgress ||
        (isAttemptsLimited && score < MAX_SCORE && hasReanswerableQuestions && !isTimerStopped)
          ? this.renderContinueCourseButton()
          : ''}
        {!isScormMode && !isLti && this.renderCloseCourseButton()}
        {!isAttemptsLimited &&
          !isPassed &&
          isPreviewMode &&
          isReviewMode &&
          this.submitAtOnceStartNewAttemptButton()}
        {!isPreviewMode &&
          !isReviewMode &&
          !isAttemptsLimited &&
          isPassed &&
          this.renderStartNewAttemptButton()}
      </>
    );
  }

  renderDownloadCertificateButton() {
    const { courseHasBeenContinued, certificateDownloaded } = this.props;
    return (
      <ButtonWrapper>
        <StatusTooltip
          popoverPosition={'top'}
          layout={TOOLTIP.INCORRECT}
          onRequestClose={this.hidePopover}
          showTooltip={this.state.showCertificateLoadedError}
          message={localize('[certificate failed load]')}
          popoverButton={
            <Button
              type="submit"
              layout={courseHasBeenContinued || certificateDownloaded ? 'secondary' : 'primary'}
              tabIndex={0}
              onClick={this.downloadCertificate}
              isLoading={this.state.isCertificateLoading}
              onBlur={this.hidePopover}
            >
              {localize('[download certificate]')}
            </Button>
          }
        />
      </ButtonWrapper>
    );
  }

  renderCloseCourseButton() {
    return (
      <ButtonWrapper>
        <Button
          layout={'secondary'}
          tabIndex={0}
          onClick={this.closeCourse}
          data-test="close-course-button"
        >
          {localize('[close course]')}
        </Button>
      </ButtonWrapper>
    );
  }

  renderContinueCourseButton() {
    return (
      <ButtonWrapper>
        <Button tabIndex={0} onClick={this.continueCourse} data-test="continue-course-button">
          {localize('[continue course]')}
        </Button>
      </ButtonWrapper>
    );
  }

  renderStartNewAttemptButton() {
    return (
      <Link
        text={localize('[start new attempt]')}
        layout={LINK.LAYOUT.FORM}
        onClick={this.openStartNewAttemptPopup}
        tabIndex={0}
        opacity={this.props.isContrastTheme ? 0.8 : 0.4}
      />
    );
  }

  submitAtOnceStartNewAttemptButton() {
    return (
      <ButtonWrapper>
        <Button layout={BUTTON.LAYOUT.PRIMARY} tabIndex={0} onClick={this.startNewAttempt}>
          {localize('[start new attempt]')}
        </Button>
        <RemainingAttemptsBlock isResultsPage={true} />
      </ButtonWrapper>
    );
  }

  renderSubmitResultsButton() {
    const { isPassed } = this.props;

    return (
      <ButtonWrapper>
        <Button
          layout={isPassed ? 'primary' : 'secondary'}
          tabIndex={0}
          onClick={this.submitResults}
        >
          {localize('[submit results]')}
        </Button>
      </ButtonWrapper>
    );
  }

  renderMyCourseButton() {
    const { learnStorageUrl, navigationActions } = this.props;
    return (
      <ButtonWrapper>
        <Button
          layout="primary"
          tabIndex={0}
          onClick={() => navigationActions.goToUrl(learnStorageUrl)}
          data-test="my-courses-button"
        >
          {localize('[my courses]')}
        </Button>
      </ButtonWrapper>
    );
  }

  renderMasteryScoreOverallFigure = () => {
    const { affectProgressQuestionsCount, masteryScore } = this.props;
    if (affectProgressQuestionsCount > 0) {
      return <CourseProgressResult masteryScore={masteryScore} />;
    }
    return <Trophy />;
  };

  mainFigure = () => {
    const { isPassed, isInProgress, isFailed, isOverallMasteryScore } = this.props;
    return isOverallMasteryScore ? (
      <Figure>{this.renderMasteryScoreOverallFigure()}</Figure>
    ) : (
      <Figure>
        {isPassed && <Trophy />}
        {isInProgress && <Rocket />}
        {isFailed && <SadResult />}
      </Figure>
    );
  };

  headerText = () => {
    const { isPassed, isInProgress, isFailed } = this.props;
    return (
      <Header appearance="h2" id={SKIP_TO_CONTENT_IDS.RESULT_PAGE} ignoreFocus={true}>
        {isPassed && localize('[congratulations]')}
        {isInProgress && localize('[course is not passed yet]')}
        {isFailed && localize('[course is not passed]')}
      </Header>
    );
  };

  coursePassStatusText = () => {
    const {
      shouldSubmitAllQuestions,
      isOverallMasteryScore,
      score,
      affectProgressQuestionsCount
    } = this.props;

    if (shouldSubmitAllQuestions && isOverallMasteryScore && affectProgressQuestionsCount > 0) {
      return localize('[you successfully passed the course with a score of x]', { score });
    }
    return localize('[you passed the course successfully]');
  };

  courseStatusText = () => {
    const { isPassed, isInProgress, isFailed } = this.props;
    return (
      <StyledText data-test="course-status-message">
        {isPassed && this.coursePassStatusText()}
        {isInProgress && localize('[you have not passed the course yet]')}
        {isFailed && localize('[you have not passed the course]')}
      </StyledText>
    );
  };

  canNavigateToMyCourses = () => {
    const { isAnonymous, isFailed, isScormMode, isPreviewMode, isLti } = this.props;
    return !isAnonymous && isFailed && !isScormMode && !isPreviewMode && !isLti;
  };

  showRemainingAttemptsText = () => {
    const { isAttemptsLimited, isFailed } = this.props;
    return (
      isAttemptsLimited &&
      isFailed && (
        <StyledText data-test="no-attempts-remaining-message">
          {`${localize('[all attempts has been used]')}`}
        </StyledText>
      )
    );
  };

  showCourseScoringText = () => {
    const {
      isOverallMasteryScore,
      hasScoreContentPages,
      affectProgressQuestionsCount,
      correctlyAnsweredQuestionsCount,
      sectionsPassedCount,
      sectionCount
    } = this.props;

    if (isOverallMasteryScore) {
      if (!hasScoreContentPages) {
        return (
          <StyledText data-test="course-score-message">
            {localize('[you correctly answered X questions out of Y]', {
              correctQuestionsCount: correctlyAnsweredQuestionsCount,
              totalQuestionsCount: affectProgressQuestionsCount
            })}
          </StyledText>
        );
      }
      return (
        <StyledText data-test="course-score-message">
          {localize('[you correctly completed X items out of Y]', {
            correctQuestionsCount: correctlyAnsweredQuestionsCount,
            totalQuestionsCount: affectProgressQuestionsCount
          })}
        </StyledText>
      );
    }
    return (
      <StyledText data-test="course-score-message">
        {localize('[you completed X out of Y sections]', {
          correctSectionsCount: sectionsPassedCount,
          totalSectionsCount: sectionCount
        })}
      </StyledText>
    );
  };

  render() {
    const {
      isPassed,
      isScormMode,
      isLti,
      isAllowedDownloadCertificate,
      userAuthorized,
      isCrossDeviceSavingEnabled,
      affectProgressSectionsCount,
      logoUrl,
      logoShown,
      shouldSubmitAllQuestions
    } = this.props;
    return (
      <Container logoShown={logoShown}>
        {logoShown && <CourseLogo logoUrl={logoUrl} />}
        {this.mainFigure()}
        {this.headerText()}
        {this.courseStatusText()}
        &nbsp;
        {shouldSubmitAllQuestions &&
          !this.hasRemainingAttempts() &&
          this.showRemainingAttemptsText()}
        &nbsp;
        {!shouldSubmitAllQuestions && this.showRemainingAttemptsText()}
        {affectProgressSectionsCount > 0 && this.showCourseScoringText()}
        <Container>
          <WidthLimiterContainer>
            {isAllowedDownloadCertificate &&
              isPassed &&
              userAuthorized &&
              isCrossDeviceSavingEnabled &&
              !isScormMode &&
              this.renderDownloadCertificateButton()}
            {!shouldSubmitAllQuestions && this.renderQuestionSubmitButton()}
            {this.canNavigateToMyCourses() && this.renderMyCourseButton()}
            {shouldSubmitAllQuestions && this.renderSubmitAtOnceButton()}
            {(isScormMode || isLti) && this.renderSubmitResultsButton()}
          </WidthLimiterContainer>
          <CourseAfterword />
        </Container>
        <SeparatorElement />
      </Container>
    );
  }
}

function mapStateToProps(state: RootAppState) {
  return {
    isPassed: courseSelectors.isPassed(state),
    isPreviewMode: settingSelectors.isPreviewMode(state),
    isReviewMode: settingSelectors.isReviewMode(state),
    title: courseSelectors.getCourseTitle(state),
    affectProgressQuestionsCount: getAffectProgressQuestionsCount(state),
    affectProgressSectionsCount: getAffectProgressSectionsCount(state),
    correctlyAnsweredQuestionsCount: getCorrectlyAffectProgressAnsweredQuestionsCount(state),
    isScormMode: settingSelectors.isScormMode(state),
    isLti: isLtiInitialized(state),
    courseId: courseSelectors.getCourseId(state),
    templateId: courseSelectors.getTemplateId(state),
    score: courseSelectors.getScore(state),
    logoUrl: settingSelectors.getLogo(state),
    logoShown: settingSelectors.isMainLogoShown(state),
    isCrossDeviceSavingEnabled: settingSelectors.isCrossDeviceSavingEnabled(state),
    learnStorageUrl: settingSelectors.getLearnServiceUrl(state),
    selectedLanguage: settingSelectors.getSelectedLanguage(state),
    courseHasBeenContinued: courseSelectors.hasBeenContinued(state),
    userAuthorized: isAuthorized(state),
    isAllowedDownloadCertificate: settingSelectors.isAllowedDownloadCertificate(state),
    certificateDownloaded: courseSelectors.isCertificateDownloaded(state),
    isNpsEnabled: settingSelectors.isNpsEnabled(state),
    isOverallMasteryScore: settingSelectors.isOverallMasteryScore(state),
    hasScoreContentPages: hasScoreContentPages(state),
    sectionsPassedCount: getSectionsPassedCount(state),
    isInProgress: courseSelectors.isInProgress(state),
    isAttemptsLimited: settingSelectors.isAttemptsLimited(state),
    masteryScore: settingSelectors.getMasteryScoreValue(state),
    hasReanswerableQuestions: hasReAnswerableQuestions(state),
    shouldSubmitAllQuestions: settingSelectors.shouldSubmitAllQuestions(state),
    limitedAttemptsNumber: settingSelectors.getLimitedAttemptsNumber(state),
    isAnonymous: isAnonymous(state),
    isFailed: courseSelectors.isFailed(state),
    attemptsUsed: courseSelectors.getCourseAttempt(state),
    timerStatus: getTimerStatus(state),
    sectionCount: getSectionsCount(state),
    questionToNavigate: getFirstIncorrectOrPendingQuestion(state),
    isContrastTheme: isContrastTheme(state)
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    courseActions: bindActionCreators(courseActions, dispatch),
    navigationActions: bindActionCreators(navigationActions, dispatch),
    popupActions: bindActionCreators(popupActions, dispatch),
    sectionActions: bindActionCreators(sectionActions, dispatch),
    timerActions: bindActionCreators(courseTimerActions, dispatch)
  };
}

export default withNavigation(connect(mapStateToProps, mapDispatchToProps)(ResultsPage));
